import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, ArrowRight, MessageCircle } from "lucide-react";
import Seo from "../components/Seo";
import { AnswerRow } from "../components/askcassie/AnswerRow";
import { AdviceNotice } from "../components/askcassie/AdviceNotice";
import { trackAsk, trackAskView } from "../lib/askTrack";

const askCassie = (question) =>
  window.dispatchEvent(new CustomEvent("cass:ask-cassie", { detail: { question } }));

export default function AskCassieCategory({ category }) {
  const answers = category.answers || [];
  useEffect(() => {
    trackAskView("category", { category: category.key || "", slug: category.key || "" });
  }, [category.key]);
  // A link to a quick answer carries the answer with it (?a=slug), so the
  // visitor lands on that answer expanded rather than at the top of the list.
  const wanted = new URLSearchParams(window.location.search).get("a")
    || (window.location.hash || "").replace(/^#a-/, "");
  return (
    <div className="bg-white">
      <Seo
        title={`${category.name} — Ask Cassie`}
        description={category.blurb}
        noindex={category.noindex}
      />

      <div className="max-w-3xl mx-auto px-5 sm:px-8 pt-10 pb-20 md:pt-14">
        <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1.5 text-xs text-slate-400" data-testid="ask-category-breadcrumb">
          <Link to="/" className="hover:text-navy">Home</Link>
          <ChevronRight size={12} />
          <Link to="/ask-cassie" className="hover:text-navy">Ask Cassie</Link>
          <ChevronRight size={12} />
          <span className="text-slate-500">{category.name}</span>
        </nav>

        <h1 className="font-display font-extrabold text-navy text-3xl sm:text-4xl lg:text-5xl mt-6 leading-[1.1]">
          {category.name}
        </h1>
        <p className="text-slate-600 text-base md:text-lg mt-4 leading-relaxed">{category.blurb}</p>

        <AdviceNotice className="mt-6" />

        <div className="mt-8" data-testid="ask-category-answers">
          {answers.length === 0 ? (
            <p className="text-sm text-slate-400">No answers here yet — Cassie can still help in the meantime.</p>
          ) : (
            answers.map((a) => (
              <AnswerRow
                key={a.slug}
                a={a}
                defaultOpen={a.slug === wanted}
                highlight={a.slug === wanted}
                onOpen={(slug) => trackAsk("open", { page: "category", slug, category: category.key || "" })}
              />
            ))
          )}
        </div>

        <div className="mt-12 flex flex-wrap items-center gap-4">
          <Link
            to="/ask-cassie"
            data-testid="ask-category-back"
            className="inline-flex items-center gap-2 text-navy font-bold text-sm hover:text-orange transition-colors"
          >
            <ArrowRight size={15} className="rotate-180" /> All subjects
          </Link>
          <button
            type="button"
            onClick={() => {
              trackAsk("outbound", { page: "category", target: "cassie", category: category.key || "" });
              askCassie(`I have a question about ${category.name.toLowerCase()}`);
            }}
            data-testid="ask-category-cassie"
            className="inline-flex items-center gap-2 border border-slate-200 hover:border-orange hover:text-orange-dark text-slate-600 font-bold text-sm px-5 py-2.5 rounded-full transition-colors"
          >
            <MessageCircle size={15} /> Ask Cassie something else
          </button>
        </div>
      </div>
    </div>
  );
}
